import { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-apollo';

import {
  CARD_ELEMENT_TYPE,
  ORIENTATION,
} from '../../../../../../services/cards/constants';
import getPrintSizeCard from '../../../../../../services/prints/graphql/queries/getPrintSizeCard';
import useAccountCardConfigure from '../../../../../../services/cards/views/AccountCardConfigurator/useAccountCardConfigure';
import getGlobalPrintProductAlbumCustomPrint from '../../../../../../services/prints/graphql/queries/getGlobalPrintProductAlbumCustomPrint';
import { getAutoFitPositionPhotoCropbox } from '../../../../../../services/cards/components/CardElementPhoto/helpers';
import { getMaxThumb } from '../../../../../../util/photo';
import getPrintPartners from '../../../../../../services/cards/graphql/queries/getPrintPartners';
import getPrintProducts from '../../../../../../services/cards/graphql/queries/getPrintProducts';

/**
 * PrintProduct configuration hook.
 *
 * @param {Object} options The options.
 * @param {Object} [options.defaultPhoto] A Photo type object
 * @param {string} [options.defaultAccountCardId] An Account Card ID as an optional default.
 * @param {string} [options.defaultOrientation] Default orientation.
 * @param {Object} [options.presetCardElementConfigurationInputs] Default card element configurations, mainly used for asset images..
 * @param {Boolean} [options.storeFetchedAccountCard] whether or not to store the fetched card by the useCardConfigure hook
 * @param {Object} [options.onError] Method to be called in the instance of an error.
 * @returns {{loadingPrintProduct: boolean, standardPrintProductConfiguration: {handlePrintProductSizeSelect: handlePrintProductSizeSelect, defaultPrintProductSize: undefined, selectedPrintProductMount: undefined, handlePrintProductMountSelect: handlePrintProductMountSelect}, accountCardConfiguration: {accountCard, onElementConfigurationChange: (function(*=, *=): Promise<void>), cardElementLoading: *, loadingAccountCard: (boolean|boolean)}, standardPrintProduct: *, globalPrintProductAlbumCustomPrint}}
 */
const useDownloadConfiguration = ({
  defaultPhoto,
  defaultAccountCardId,
  defaultOrientation,
  presetCardElementConfigurationInputs,
  storeFetchedAccountCard,
  onError,
}) => {
  const [selectedCardSide, setSelectedCardSide] = useState();
  const [recalculateCropbox, setRecalculateCropbox] = useState(false);
  const { data: { globalAlbumCustomPrint } = {} } = useQuery(
    getGlobalPrintProductAlbumCustomPrint,
  );

  const { data: { printPartners } = {}, loading } = useQuery(getPrintPartners, {
    variables: {
      name: 'Bay Photo',
    },
    fetchPolicy: 'cache-first',
  });

  const [bayPhoto] = printPartners?.records ?? [];
  const { data: { printProducts } = {} } = useQuery(getPrintProducts, {
    variables: { name: 'Standard Prints', print_partner_id: bayPhoto?.id },
    fetchPolicy: 'cache-first',
    skip: !bayPhoto,
  });

  const standardPrintProduct = printProducts?.records?.[0];
  const defaultPrintProductSize = useMemo(
    () =>
      standardPrintProduct?.sizes?.records?.find(
        ({ print_size: printSize }) =>
          printSize.width === 8 && printSize.height === 10,
      ),
    [standardPrintProduct],
  );

  const { loading: loadingCard, data: { card } = {} } = useQuery(
    getPrintSizeCard,
    {
      variables: {
        card_id: standardPrintProduct?.card?.id,
        print_size_id: defaultPrintProductSize?.print_size?.id,
        orientation: defaultOrientation,
      },
      skip: !standardPrintProduct?.card?.id || !defaultPrintProductSize,
    },
  );

  const {
    onCardChange,
    accountCard,
    loadingAccountCard,
    onElementConfigurationChange,
    cardElementLoading,
  } = useAccountCardConfigure({
    accountCardId: defaultAccountCardId,
    presetCardElementConfigurationInputs: (() => {
      if (defaultAccountCardId) {
        return undefined;
      }

      if (defaultPhoto) {
        return [
          {
            cardElement: {
              type: CARD_ELEMENT_TYPE.PHOTO,
            },
            cardElementConfiguration: {
              photo_id: defaultPhoto.id,
            },
            calculateCropbox: (element, printSize) => {
              const canonicalWidth =
                defaultOrientation === ORIENTATION.LANDSCAPE
                  ? Math.max(printSize.width, printSize.height)
                  : Math.min(printSize.width, printSize.height);
              const canonicalHeight =
                defaultOrientation === ORIENTATION.LANDSCAPE
                  ? Math.min(printSize.width, printSize.height)
                  : Math.max(printSize.width, printSize.height);

              return getAutoFitPositionPhotoCropbox({
                cardElement: element.cardElement,
                cardWidth: canonicalWidth,
                cardHeight: canonicalHeight,
                thumb: getMaxThumb(defaultPhoto?.thumbnail_urls),
                faceboxes: (defaultPhoto?.faces?.records ?? []).map(
                  ({ facebox }) => facebox,
                ),
              });
            },
          },
        ];
      }

      return presetCardElementConfigurationInputs;
    })(),
    storeFetchedAccountCard,
    onError,
  });

  useEffect(() => {
    // Select the first CardSide so that there's always something rendered to the user.
    // When we add more CardSides the user will also be able to select their own (change template)
    // via a UI that renders the CardSides and their previews.
    if (card) {
      const [cardSideGroup] = card.card_side_groups.records ?? [];
      const [cardSide] = cardSideGroup.card_sides.records ?? [];
      setSelectedCardSide(cardSide);
    }
  }, [card]);

  // For this use case, we always want a default photo.
  useEffect(() => {
    if (card && defaultPhoto && selectedCardSide && !defaultAccountCardId) {
      // If a defaultAccountCardId is provided, we're likely
      // updating, as such, we don't need to call this method.
      onCardChange(
        {
          card_id: card?.id,
          front_card_side_id: selectedCardSide.id,
          print_product_configuration: {
            print_product_id: standardPrintProduct.id,
            print_product_size_id: defaultPrintProductSize?.id,
          },
        },
        true,
      );
    }
  }, [card?.id, defaultPhoto, selectedCardSide?.id]);

  useEffect(() => {
    const { print_size: printSize } = defaultPrintProductSize ?? {};
    if (accountCard && printSize && recalculateCropbox) {
      const [accountCardSide] = accountCard?.account_card_sides ?? [];
      const {
        side,
        card_side: cardSide,
        card_element_configurations: cardElementConfigurations,
      } = accountCardSide ?? {};
      const { orientation } = cardSide?.card_template ?? {};
      const cardElement = cardSide?.card_template?.card_elements?.find(
        ({ type }) => type === CARD_ELEMENT_TYPE.PHOTO,
      );
      const cardElementConfiguration = (cardElementConfigurations ?? []).find(
        config => config.card_element.id === cardElement?.id,
      );
      const { photo } = cardElementConfiguration ?? {};

      if (side && cardElement && cardElementConfiguration && photo) {
        const canonicalWidth =
          orientation === ORIENTATION.LANDSCAPE
            ? Math.max(printSize.width, printSize.height)
            : Math.min(printSize.width, printSize.height);

        const canonicalHeight =
          orientation === ORIENTATION.LANDSCAPE
            ? Math.min(printSize.width, printSize.height)
            : Math.max(printSize.width, printSize.height);

        onElementConfigurationChange(
          {
            cardElement,
            cardElementConfiguration,
            side,
          },
          {
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth: canonicalWidth,
              cardHeight: canonicalHeight,
              thumb: getMaxThumb(photo.thumbnail_urls),
              faceboxes: (photo.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
            }),
          },
          accountCard,
        );
        setRecalculateCropbox(false);
      }
    }
  }, [accountCard, recalculateCropbox]);

  const cardSides =
    card?.card_side_groups?.records?.[0]?.card_sides?.records ?? [];
  const handleCardSideSelect = newCardSideId => {
    const newCardSide = cardSides?.find(
      cardSide => cardSide.id === newCardSideId,
    );

    if (newCardSide) {
      setSelectedCardSide(newCardSide);
    }
  };

  return {
    printProduct: standardPrintProduct,
    globalPrintProductAlbumCustomPrint: globalAlbumCustomPrint,
    loadingPrintProduct: loading,
    standardPrintProductConfiguration: {
      defaultPrintProductSize,
      selectedCardSide,
      handleCardSideSelect,
    },
    accountCardConfiguration: {
      loadingAccountCard: loadingCard || loadingAccountCard,
      accountCard,
      cardSides,
      onElementConfigurationChange,
      cardElementLoading,
    },
  };
};

export default useDownloadConfiguration;
